import { useEffect, useEffectEvent, useRef } from 'react'
import type { ProFormInstance } from '@ant-design/pro-components'

export type PageTableSearch = {
  current: number
  pageSize: number
}

export type PageTableNavigate<TSearch extends PageTableSearch> = (options: {
  replace?: boolean
  search: TSearch | ((previous: TSearch) => TSearch)
}) => unknown

export function usePageTableNavigation<
  TSearch extends PageTableSearch,
  TFormValues extends Record<string, unknown> = Partial<TSearch>
>({
  defaultSearch,
  getFormInitialValues,
  getSearchFromForm,
  navigate,
  onQueryChange,
  search,
  showSizeChanger = true,
  showTotal
}: {
  defaultSearch: TSearch
  getFormInitialValues?: (search: TSearch) => Partial<TFormValues>
  getSearchFromForm?: (values: TFormValues) => Partial<TSearch>
  navigate: PageTableNavigate<TSearch>
  onQueryChange?: () => void
  search: TSearch
  showSizeChanger?: boolean
  showTotal: (total: number) => string
}) {
  const formRef = useRef<ProFormInstance<TFormValues> | undefined>(undefined)
  const formInitialValues = getFormInitialValues?.(search) ?? (search as unknown as Partial<TFormValues>)
  const searchKey = JSON.stringify(search)
  const syncedSearchKey = useRef<string | undefined>(undefined)

  const syncForm = useEffectEvent(() => {
    const form = formRef.current
    if (!form || syncedSearchKey.current === searchKey) {
      return
    }
    syncedSearchKey.current = searchKey
    form.setFieldsValue(formInitialValues)
  })

  useEffect(() => {
    syncForm()
  }, [searchKey])

  const handleReset = () => {
    onQueryChange?.()
    void navigate({ replace: true, search: defaultSearch })
  }

  const handleSubmit = (values: TFormValues) => {
    onQueryChange?.()
    const patch = getSearchFromForm ? getSearchFromForm(values) : (values as Partial<TSearch>)
    void navigate({
      replace: true,
      search: (previous) => ({ ...previous, ...patch, current: 1 })
    })
  }

  const handlePageChange = (current: number, pageSize: number) => {
    onQueryChange?.()
    void navigate({ search: (previous) => ({ ...previous, current, pageSize }) })
  }

  const pagination = {
    current: search.current,
    onChange: handlePageChange,
    pageSize: search.pageSize,
    showSizeChanger,
    showTotal
  }

  return {
    formInitialValues,
    formRef,
    onReset: handleReset,
    onSubmit: handleSubmit,
    pagination
  }
}
